import { N, round, ceil } from './helpers.mjs';

export const BUSINESS = [
  // ====================== BUSINESS ESSENTIALS ======================
  {
    slug: 'vat-add-remove',
    trade: 'business-essentials',
    name: 'VAT Calculator',
    blurb: 'Add or remove UK VAT (20% standard) on any amount.',
    inputs: [
      { key: 'amount', label: 'Amount', unit: '£', placeholder: '1000' },
      { key: 'vat', label: 'VAT rate', unit: '%', placeholder: '20', default: 20 },
    ],
    compute: (v) => {
      const N = (x) => { const n = parseFloat(x); return Number.isFinite(n) ? n : 0; };
      const round = (x, d = 2) => Math.round(x * Math.pow(10, d)) / Math.pow(10, d);
      const net = N(v.amount);
      const rate = N(v.vat) / 100;
      const vat = net * rate;
      const gross = net + vat;
      // Reverse: if entered amount IS gross, work out net
      const netFromGross = rate > 0 ? net / (1 + rate) : net;
      const vatFromGross = net - netFromGross;
      return [
        { label: 'NET → +VAT amount', value: '£' + round(vat).toLocaleString(), unit: '' },
        { label: 'NET → GROSS (incl. VAT)', value: '£' + round(gross).toLocaleString(), unit: '' },
        { label: 'GROSS → NET (excl. VAT)', value: '£' + round(netFromGross).toLocaleString(), unit: '' },
        { label: 'GROSS → VAT element', value: '£' + round(vatFromGross).toLocaleString(), unit: '' },
      ];
    },
  },
  {
    slug: 'markup-margin',
    trade: 'business-essentials',
    name: 'Markup ↔ Margin Converter',
    blurb: 'Avoid the classic trap. Markup is added to cost; margin is taken from selling price.',
    inputs: [
      { key: 'cost', label: 'Material / job cost', unit: '£', placeholder: '500' },
      { key: 'markup', label: 'Markup', unit: '%', placeholder: '25', default: 25 },
    ],
    compute: (v) => {
      const N = (x) => { const n = parseFloat(x); return Number.isFinite(n) ? n : 0; };
      const round = (x, d = 2) => Math.round(x * Math.pow(10, d)) / Math.pow(10, d);
      const cost = N(v.cost);
      const mu = N(v.markup) / 100;
      const sell = cost * (1 + mu);
      const profit = sell - cost;
      const margin = sell > 0 ? (profit / sell) * 100 : 0;
      return [
        { label: 'Selling price', value: '£' + round(sell).toLocaleString(), unit: '' },
        { label: 'Profit', value: '£' + round(profit).toLocaleString(), unit: '' },
        { label: 'Actual margin %', value: round(margin, 1), unit: '%' },
      ];
    },
  },
  {
    slug: 'day-rate-target',
    trade: 'business-essentials',
    name: 'Day Rate Calculator',
    blurb: 'Reverse-engineer your day rate from a target take-home + overheads.',
    inputs: [
      { key: 'salary', label: 'Target annual take-home', unit: '£', placeholder: '35000' },
      { key: 'overheads', label: 'Annual overheads', unit: '£', placeholder: '8000' },
      { key: 'daysOff', label: 'Days off per year (holidays, sick)', unit: '', placeholder: '40', default: 40 },
      { key: 'taxRate', label: 'Combined tax + NI', unit: '%', placeholder: '28', default: 28 },
    ],
    compute: (v) => {
      const N = (x) => { const n = parseFloat(x); return Number.isFinite(n) ? n : 0; };
      const ceil = Math.ceil;
      const round = (x, d = 2) => Math.round(x * Math.pow(10, d)) / Math.pow(10, d);
      const workingDays = Math.max(260 - N(v.daysOff), 1);
      const taxFactor = 1 / (1 - N(v.taxRate) / 100); // gross-up
      const grossNeeded = N(v.salary) * taxFactor + N(v.overheads);
      const dayRate = grossNeeded / workingDays;
      return [
        { label: 'Working days per year', value: workingDays, unit: 'days' },
        { label: 'Gross income needed', value: '£' + round(grossNeeded).toLocaleString(), unit: '' },
        { label: 'Minimum day rate', value: '£' + ceil(dayRate).toLocaleString(), unit: '/day' },
        { label: 'Minimum hourly (8h)', value: '£' + ceil(dayRate / 8).toLocaleString(), unit: '/hr' },
      ];
    },
  },
  {
    slug: 'quote-net-margin',
    trade: 'business-essentials',
    name: 'Quote Profit Check',
    blurb: 'What\'s actually left after costs, VAT and tax on a quote you\'ve already given?',
    inputs: [
      { key: 'quoteGross', label: 'Quote total (incl. VAT)', unit: '£', placeholder: '3600' },
      { key: 'materials', label: 'Materials cost', unit: '£', placeholder: '900' },
      { key: 'labourDays', label: 'Labour days', unit: '', placeholder: '3' },
      { key: 'labourCost', label: 'Labour cost per day', unit: '£', placeholder: '180', default: 180 },
      { key: 'vat', label: 'VAT rate', unit: '%', placeholder: '20', default: 20 },
      { key: 'taxRate', label: 'Income tax + NI', unit: '%', placeholder: '28', default: 28 },
    ],
    compute: (v) => {
      const N = (x) => { const n = parseFloat(x); return Number.isFinite(n) ? n : 0; };
      const round = (x, d = 2) => Math.round(x * Math.pow(10, d)) / Math.pow(10, d);
      const gross = N(v.quoteGross);
      const net = gross / (1 + N(v.vat) / 100);
      const costs = N(v.materials) + N(v.labourDays) * N(v.labourCost);
      const profitBeforeTax = net - costs;
      const tax = Math.max(profitBeforeTax * (N(v.taxRate) / 100), 0);
      const takeHome = profitBeforeTax - tax;
      const pct = gross > 0 ? (takeHome / gross) * 100 : 0;
      return [
        { label: 'Net (excl. VAT)', value: '£' + round(net).toLocaleString(), unit: '' },
        { label: 'Total costs', value: '£' + round(costs).toLocaleString(), unit: '' },
        { label: 'Profit before tax', value: '£' + round(profitBeforeTax).toLocaleString(), unit: '' },
        { label: 'Tax + NI owed', value: '£' + round(tax).toLocaleString(), unit: '' },
        { label: 'Take-home', value: '£' + round(takeHome).toLocaleString(), unit: '' },
        { label: 'Take-home %', value: round(pct, 1), unit: '%' },
      ];
    },
  },

];
